/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useServiceContext } from '../../../../root'
import { useNavigateWithParams } from '../../../../hooks/useNavigateWithParams'
import LoadingSpinner from '../../../../components/LoadingSpinner'
import { Task } from '../../../../services/api/types'
import TaskManageDocsTableMenu from './TaskManageDocsTableMenu'
import { getFileDetail, getFsiPath } from '~/services/api/files'
import { toast } from '~/services/toast'
import { buildUrlWithCSelections } from '~/utils/navigation'

interface TaskFile {
  draftFsi?: {
    id: number
    name: string
    targetParentFolderName?: string
  }
  sourceFsi?: any
  documentTypeTemplateId?: number
  documentTypeTemplateName?: string
}

interface TaskManageDocsTableProps {
  task: Task | null
  workflow: any
  doClose?: () => void
}

/**
 * Task Manage Documents Table
 * Displays all files in a multi-file workflow with individual actions
 * Migrated from: lego/page/tasks/manage-docs/TaskManageDocsTable.jsx
 */
export default function TaskManageDocsTable({ task, workflow, doClose }: TaskManageDocsTableProps) {
  const { t } = useTranslation()
  const { api } = useServiceContext()
  const navigate = useNavigateWithParams()
  const [loading, setLoading] = useState(true)
  const [taskFiles, setTaskFiles] = useState<TaskFile[]>([])

  useEffect(() => {
    const loadFiles = async () => {
      if (workflow && Array.isArray(workflow.files)) {
        const promises: Promise<{ file: TaskFile }>[] = []
        const docTypesIds: number[] = []

        workflow.files.forEach((item: TaskFile) => {
          if (item.draftFsi) {
            promises.push(
              new Promise((resolve, reject) => {
                api.files
                  .getFileMetadata(item.draftFsi!.id)
                  .then((data: any) => {
                    docTypesIds.push(data.documentTypeTemplateId)
                    resolve({
                      file: {
                        ...item,
                        documentTypeTemplateId: data.documentTypeTemplateId
                      }
                    })
                  })
                  .catch(() => {
                    reject({ file: item })
                  })
              })
            )
          }
        })

        if (promises.length > 0) {
          try {
            const results = await Promise.allSettled(promises)
            const files = results.map((r) =>
              r.status === 'fulfilled' ? r.value.file : (r as PromiseRejectedResult).reason.file
            )

            if (docTypesIds.length > 0) {
              try {
                const docTypesData: any = await api.documentTypes.getAllForTable({ id: docTypesIds })
                files.forEach((item) => {
                  const docType = docTypesData.elements?.find((dt: any) => dt.id === item.documentTypeTemplateId)
                  item.documentTypeTemplateName = docType?.name || null
                })
              } catch (error) {
                console.error('Failed to load document types:', error)
              }
            }

            setTaskFiles(files)
            setLoading(false)
          } catch (error) {
            console.error('Failed to load files:', error)
            setLoading(false)
          }
        } else {
          setLoading(false)
        }
      } else {
        setLoading(false)
      }
    }

    loadFiles()
  }, [workflow, api])

  const handleShowFile = async (fileId: number) => {
    try {
      // Replicate the loader's checks
      await Promise.all([getFileDetail(fileId), getFsiPath(fileId, true)])
      navigate(`/show-file/${fileId}`)
    } catch (error: any) {
      toast.info(
        error?.status === 401
          ? 'Unauthorized access to the document.'
          : error?.status === 403
            ? 'GENERATING_VIEWABLE_DOC_NOT_ACCESS_MSG'
            : 'Failed to load file details.'
      )
      // Do not navigate
    }
  }

  if (loading) {
    return <LoadingSpinner centered size={40} />
  }

  return (
    <table className="styled-table chita-table border-none">
      <thead>
        <tr>
          <th>{t('NAME')}</th>
          <th>{t('FOLDER')}</th>
          <th>{t('ACTIONS')}</th>
        </tr>
      </thead>
      <tbody>
        {taskFiles.map((file) => (
          <tr key={file.draftFsi?.id}>
            <td className="clipped-cell">
              <a
                href={file.draftFsi?.id ? buildUrlWithCSelections(`/show-file/${file.draftFsi.id}`) : '#'}
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey || e.button !== 0) return
                  e.preventDefault()
                  if (file.draftFsi?.id) {
                    handleShowFile(file.draftFsi.id)
                  }
                }}
                title={file.draftFsi?.name}
                className="d-block ellipsis"
              >
                {file.draftFsi?.name}
              </a>
            </td>
            <td>{file.draftFsi?.targetParentFolderName || t('NOT_AVAILABLE')}</td>
            <td className="actions">
              <TaskManageDocsTableMenu task={task} workflow={workflow} file={file} doClose={doClose} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
